import { FilesetResolver } from '@mediapipe/tasks-vision';
import type { TryOnConfig } from './TryOnApp';
import type { FrameSpec } from '../config/frames';

const warmed = new Set<string>();
const images = new Map<string, HTMLImageElement>();
/** Downloads only. Never creates a tracker, WebGL context or camera stream. */
export function warmSelectedFrame(frame: FrameSpec): void {
  const silhouette = frame.silhouette;
  for (const photo of [silhouette?.frontPhoto, silhouette?.templePhoto, silhouette?.stitchPhoto]) {
    if (!photo || images.has(photo.dataUrl)) continue;
    const image = new Image();
    image.decoding = 'async';
    image.onerror = () => images.delete(photo.dataUrl);
    images.set(photo.dataUrl, image);
    image.src = photo.dataUrl;
  }
  // Retain only a small decoded-image cache, not the entire catalogue.
  while (images.size > 6) images.delete(images.keys().next().value!);
}

export async function warmEngine(config: Partial<TryOnConfig>): Promise<void> {
  const connection = (navigator as Navigator & { connection?: { saveData?: boolean; effectiveType?: string } }).connection;
  if (connection?.saveData || /(^|-)2g$/.test(connection?.effectiveType ?? '')) return;
  const base = config.assetBase ?? defaultAssetBase();
  const root = new URL(base.endsWith('/') ? base : base + '/', location.href);
  const assets = config.assets ?? {};
  try {
    const vision = assets.wasmLoaderPath && assets.wasmBinaryPath
      ? { wasmLoaderPath: assets.wasmLoaderPath, wasmBinaryPath: assets.wasmBinaryPath }
      : await FilesetResolver.forVisionTasks(new URL('wasm', root).href, config.trackerMode !== 'inline');
    // The browser HTTP cache is also used by the worker. Failures remain retryable
    // by the real engine; warm-up must never block the launch button.
    await Promise.all([vision.wasmLoaderPath, vision.wasmBinaryPath,
      assets.modelAssetPath ?? new URL('models/face_landmarker.task', root).href].map(async url => {
      if (warmed.has(url)) return;
      warmed.add(url);
      try {
        const response = await fetch(url, { credentials: 'same-origin' });
        if (!response.ok) throw new Error('Preload failed');
        await response.arrayBuffer();
      } catch { warmed.delete(url); }
    }));
  } catch { /* Best effort; normal startup owns errors and fallback. */ }
}
function defaultAssetBase(): string {
  return /\.js(\?|$)/.test(import.meta.url) ? new URL('../', import.meta.url).href : import.meta.env.BASE_URL;
}
